import { TimeoutType, UserTimeoutDTO } from '../../communication/PlayerApiProtocol.generated'
import { DropdownOption } from '../../ui/common/dropdown/DropdownSelector'
import { createReducer } from '../CreateReducer'
import { AsyncState, createAsyncState, ReduxAction } from '../ReduxUtils'
import { RGTypes } from './ResponsibleGamingActions'
import { TimeoutTypeDropdownOption } from './ResponsibleGamingConstants'
import { Feature, userFeatureService } from '../../services/UserFeatureService'

export interface ResponsibleGamingState extends AsyncState {
	agreement?: boolean
	exclusionAgreement?: boolean
	gameTypeValue?: string
	gameTimeValue?: string
	exclusionValue?: string
	loadingTimeouts?: boolean
	loadedTimeouts?: boolean
	loadingLimits?: boolean
	userTimeouts?: UserTimeoutDTO[]
	userExclusion?: UserTimeoutDTO
	timeoutsError?: boolean
	exclusionsError?: boolean
	limitsError?: boolean
	availableSelections?: DropdownOption[]
	timeoutSubmitOpen?: boolean
	exclusionSubmitOpen?: boolean
	limitSubmitOpen?: boolean
	purchaseLimits?: PurchaseLimitPeriod[]
	isKycVerified?: boolean
	dailyLimitAgreement?: boolean
	weeklyLimitAgreement?: boolean
	monthlyLimitAgreement?: boolean
	currentDailyLimitAmount?: number
	currentWeeklyLimitAmount?: number
	currentMonthlyLimitAmount?: number
	dailyLimitAmount?: string
	weeklyLimitAmount?: string
	monthlyLimitAmount?: string
	maxDailyLimit?: number
	maxWeeklyLimit?: number
	maxMonthlyLimit?: number
	dailyAccumulatedPurchaseSum?: number
	weeklyAccumulatedPurchaseSum?: number
	monthlyAccumulatedPurchaseSum?: number
}

export const POKER_GAMES_OPTION =  { value: TimeoutTypeDropdownOption.POKER, display: 'Poker Games' }
export const CASINO_GAMES_OPTION =  { value: TimeoutTypeDropdownOption.CASINO, display: 'Casino Games' }
export const ALL_GAMES_OPTION =  { value: TimeoutTypeDropdownOption.ALL, display: 'All Games' }

const INITIAL_STATE: ResponsibleGamingState = createAsyncState({
	activeValue: 'responsible-gameplay.timeout.select-label',
	agreement: false,
	exclusionAgreement: false,
	loadingTimeouts: false,
	loadingLimits: false,
	userTimeouts: [],
	purchaseLimits: [],
	isKycVerified: undefined,
	availableSelections: [POKER_GAMES_OPTION, CASINO_GAMES_OPTION, ALL_GAMES_OPTION],
	timeoutSubmitOpen: false,
	exclusionSubmitOpen: false,
	limitSubmitOpen: false,
	dailyLimitAgreement: false,
	weeklyLimitAgreement: false,
	monthlyLimitAgreement: false,
	maxDailyLimit: Number.MAX_SAFE_INTEGER,
	maxWeeklyLimit: Number.MAX_SAFE_INTEGER,
	maxMonthlyLimit: Number.MAX_SAFE_INTEGER,
	dailyAccumulatedPurchaseSum: 0,
	weeklyAccumulatedPurchaseSum: 0,
	monthlyAccumulatedPurchaseSum: 0,
})

enum SystemLimit {
	DAILY_UNVERIFIED = 1000,
	WEEKLY_UNVERIFIED = 1500,
	MONTHLY_UNVERIFIED = 3000,
	MONTHLY_VERIFIED = 12000,
}

interface MaxLimitsProps {
	isKycVerified?: boolean
	currentDailyLimitAmount?: number
	currentWeeklyLimitAmount?: number
	currentMonthlyLimitAmount?: number
}

export function calculateMaxPurchaseLimits({
	isKycVerified,
	currentDailyLimitAmount,
	currentWeeklyLimitAmount,
	currentMonthlyLimitAmount,
}: MaxLimitsProps) {
	const dailyAmount = currentDailyLimitAmount || 0
	const weeklyAmount = currentWeeklyLimitAmount || 0
	const monthlyAmount = currentMonthlyLimitAmount || 0

	const monthlySystemLimit = isKycVerified ? SystemLimit.MONTHLY_VERIFIED : SystemLimit.MONTHLY_UNVERIFIED
	const maxMonthlyLimit = Math.max(monthlySystemLimit, monthlyAmount)

	const weeklySystemLimit = isKycVerified ? SystemLimit.MONTHLY_VERIFIED : SystemLimit.WEEKLY_UNVERIFIED
	const maxWeeklyLimit = Math.max(weeklySystemLimit, weeklyAmount)

	const dailySystemLimit = isKycVerified ? SystemLimit.MONTHLY_VERIFIED : SystemLimit.DAILY_UNVERIFIED
	const maxDailyLimit = Math.max(dailySystemLimit, dailyAmount)

	return { maxDailyLimit, maxWeeklyLimit, maxMonthlyLimit }
}

export function calculateMaxPurchaseLimitsWithoutDefaults() {
	return {
		maxDailyLimit: Number.MAX_SAFE_INTEGER,
		maxWeeklyLimit: Number.MAX_SAFE_INTEGER,
		maxMonthlyLimit: Number.MAX_SAFE_INTEGER,
	}
}


type TimeoutReducer<T> = (state: ResponsibleGamingState, action: ReduxAction<T>) => ResponsibleGamingState;

const submitTimeout: TimeoutReducer<{}> = (state, action) => ({
	...state,
	loadingTimeouts: true,
})

const fetchTimeouts: TimeoutReducer<{}> = (state, action) => ({
	...state,
	loadingTimeouts: true,
})

const receivedTimeouts: TimeoutReducer<any>  = (state, action) => ({
	...state,
	userTimeouts: action.payload.result.filter(t => t.timeoutType !== TimeoutType.SELF_EXCLUSION),
	userExclusion: action.payload.result.find(t => t.timeoutType === TimeoutType.SELF_EXCLUSION),
	availableSelections: filterSelections(action.payload.result),
	loadingTimeouts: false,
	loadedTimeouts: true,
})

const failedTimeouts: TimeoutReducer<{}> = (state, action) => ({
	...state,
	timeoutsError: true,
})

const failedExclusions: TimeoutReducer<{}> = (state, action) => ({
	...state,
	exclusionsError: true,
})

const fetchPurchaseLimits: TimeoutReducer<{}> = (state, action) => ({
	...state,
	loadingLimits: true,
})

const receivedPurchaseLimits: TimeoutReducer<any>  = (state, action) => {
	const maxMonthlyLimitFilter = action.payload.result.filter(l => l.timePeriod === TimePeriod.MONTH)
	const currentMonthlyLimitAmount = maxMonthlyLimitFilter.length > 0 && maxMonthlyLimitFilter[0].amount
	const monthlyAccumulatedPurchaseSum = maxMonthlyLimitFilter.length > 0 && maxMonthlyLimitFilter[0].accumulatedSum

	const maxWeeklyLimitFilter = action.payload.result.filter(l => l.timePeriod === TimePeriod.WEEK)
	const currentWeeklyLimitAmount = maxWeeklyLimitFilter.length > 0 && maxWeeklyLimitFilter[0].amount
	const weeklyAccumulatedPurchaseSum = maxWeeklyLimitFilter.length > 0 && maxWeeklyLimitFilter[0].accumulatedSum

	const maxDailyLimitFilter = action.payload.result.filter(l => l.timePeriod === TimePeriod.DAY)
	const currentDailyLimitAmount = maxDailyLimitFilter.length > 0 && maxDailyLimitFilter[0].amount
	const dailyAccumulatedPurchaseSum = maxDailyLimitFilter.length > 0 && maxDailyLimitFilter[0].accumulatedSum

	const { maxDailyLimit, maxWeeklyLimit, maxMonthlyLimit } = calculateMaxPurchaseLimitsWithoutDefaults()

	return {
		...state,
		purchaseLimits: action.payload.result,
		currentDailyLimitAmount,
		currentWeeklyLimitAmount,
		currentMonthlyLimitAmount,
		maxDailyLimit,
		maxWeeklyLimit,
		maxMonthlyLimit,
		loadingLimits: false,
		monthlyAccumulatedPurchaseSum,
		weeklyAccumulatedPurchaseSum,
		dailyAccumulatedPurchaseSum,
	}
}

const failedPurchaseLimits: TimeoutReducer<{}> = (state, action) => ({
	...state,
	limitsError: true,
})

const setKycVerificationStatus: TimeoutReducer<any> = (state, action) => ({
	...state,
	isKycVerified: action.payload.isKycVerified
})

const selectAgreement: TimeoutReducer<any> = (state, action) => ({
	...state,
	agreement: action.payload.agreement,
})

const selectExclusionAgreement: TimeoutReducer<any> = (state, action) => ({
	...state,
	exclusionAgreement: action.payload.exclusionAgreement,
})

const selectTimeoutType: TimeoutReducer<TimeoutTypeDropdownOption> = (state, action) => ({
	...state,
	gameTypeValue: action.payload,
})

const selectTimeoutPeriod: TimeoutReducer<TimeoutPeriodType> = (state, action) => ({
	...state,
	gameTimeValue: action.payload,
})

const selectExclusionPeriod: TimeoutReducer<TimeoutPeriodType> = (state, action) => ({
	...state,
	exclusionValue: action.payload,
})

const setTimeoutSubmitOpen: TimeoutReducer<any> = (state, action) => ({
	...state,
	timeoutSubmitOpen: action.payload.open,
})

const setExclusionSubmitOpen: TimeoutReducer<any> = (state, action) => ({
	...state,
	exclusionSubmitOpen: action.payload.open,
})

const setLimitSubmitOpen: TimeoutReducer<any> = (state, action) => ({
	...state,
	limitSubmitOpen: action.payload.open,
})

const setDailyLimitAgreement: TimeoutReducer<any> = (state, action) => ({
	...state,
	dailyLimitAgreement: action.payload.agreement,
	dailyLimitAmount: action.payload.agreement ? state.dailyLimitAmount : '',
})

const setWeeklyLimitAgreement: TimeoutReducer<any> = (state, action) => ({
	...state,
	weeklyLimitAgreement: action.payload.agreement,
	weeklyLimitAmount: action.payload.agreement ? state.weeklyLimitAmount : '',
})

const setMonthlyLimitAgreement: TimeoutReducer<any> = (state, action) => ({
	...state,
	monthlyLimitAgreement: action.payload.agreement,
	monthlyLimitAmount: action.payload.agreement ? state.monthlyLimitAmount : '',
})

const setDailyLimitAmount: TimeoutReducer<any> = (state, action) => ({
	...state,
	dailyLimitAmount: action.payload.amount,
})

const setWeeklyLimitAmount: TimeoutReducer<any> = (state, action) => ({
	...state,
	weeklyLimitAmount: action.payload.amount,
})

const setMonthlyLimitAmount: TimeoutReducer<any> = (state, action) => ({
	...state,
	monthlyLimitAmount: action.payload.amount,
})

const boundDailyLimitAmount: TimeoutReducer<any> = (state, action) => {
	const upperLimit = state.maxDailyLimit
	const lowerLimit = 0
	return {
		...state,
		dailyLimitAmount: Math.min(Math.max(lowerLimit, action.payload.amount), upperLimit).toString(),
	}
}

const boundWeeklyLimitAmount: TimeoutReducer<any> = (state, action) => {
	const upperLimit = state.maxWeeklyLimit
	const lowerLimit = 0
	return {
		...state,
		weeklyLimitAmount: Math.min(Math.max(lowerLimit, action.payload.amount), upperLimit).toString(),
	}
}

const boundMonthlyLimitAmount: TimeoutReducer<any> = (state, action) => {
	const upperLimit = state.maxMonthlyLimit
	const lowerLimit = 0
	return {
		...state,
		monthlyLimitAmount: Math.min(Math.max(lowerLimit, action.payload.amount), upperLimit).toString(),
	}
}

export const HANDLERS = {
	[RGTypes.FETCH_TIMEOUTS]: fetchTimeouts,
	[RGTypes.RECEIVED_TIMEOUTS]: receivedTimeouts,
	[RGTypes.FAILED_TIMEOUTS]: failedTimeouts,
	[RGTypes.FAILED_EXCLUSIONS]: failedExclusions,
	[RGTypes.SUBMIT_TIMEOUT]: submitTimeout,
	[RGTypes.SELECT_AGREEMENT]: selectAgreement,
	[RGTypes.SELECT_EXCLUSION_AGREEMENT]: selectExclusionAgreement,
	[RGTypes.SELECT_TIMEOUT_TYPE]: selectTimeoutType,
	[RGTypes.SELECT_TIMEOUT_PERIOD]: selectTimeoutPeriod,
	[RGTypes.SELECT_EXCLUSION_PERIOD]: selectExclusionPeriod,
	[RGTypes.SET_TIMEOUT_SUBMIT_OPEN]: setTimeoutSubmitOpen,
	[RGTypes.SET_EXCLUSION_SUBMIT_OPEN]: setExclusionSubmitOpen,
	[RGTypes.SET_LIMIT_SUBMIT_OPEN]: setLimitSubmitOpen,
	[RGTypes.FETCH_USER_PURCHASE_LIMITS]: fetchPurchaseLimits,
	[RGTypes.RECEIVED_USER_PURCHASE_LIMITS]: receivedPurchaseLimits,
	[RGTypes.FAILED_PURCHASE_LIMITS]: failedPurchaseLimits,
	[RGTypes.SET_KYC_VERIFICATION_STATUS]: setKycVerificationStatus,
	[RGTypes.SET_DAILY_LIMIT_AGREEMENT]: setDailyLimitAgreement,
	[RGTypes.SET_DAILY_LIMIT_AMOUNT]: setDailyLimitAmount,
	[RGTypes.BOUND_DAILY_LIMIT_AMOUNT]: boundDailyLimitAmount,
	[RGTypes.SET_WEEKLY_LIMIT_AGREEMENT]: setWeeklyLimitAgreement,
	[RGTypes.SET_WEEKLY_LIMIT_AMOUNT]: setWeeklyLimitAmount,
	[RGTypes.BOUND_WEEKLY_LIMIT_AMOUNT]: boundWeeklyLimitAmount,
	[RGTypes.SET_MONTHLY_LIMIT_AGREEMENT]: setMonthlyLimitAgreement,
	[RGTypes.SET_MONTHLY_LIMIT_AMOUNT]: setMonthlyLimitAmount,
	[RGTypes.BOUND_MONTHLY_LIMIT_AMOUNT]: boundMonthlyLimitAmount,
}

const filterSelections = (userTimeouts: UserTimeoutDTO[]): DropdownOption[] => {
	const hasExistingPokerTimeout = userTimeouts.find( t => t.timeoutType === TimeoutType.POKER)
	const hasExistingCasinoTimeout = userTimeouts.find( t => t.timeoutType === TimeoutType.CASINO)
	const isSlotsEnabled = userFeatureService.doesUserHaveFeature(Feature.SLOTS_LOBBY)
	const isPokerEnabled = userFeatureService.doesUserHaveFeature(Feature.POKER)
	const casinoOption = isSlotsEnabled ? [CASINO_GAMES_OPTION] : []
	const pokerOption = isPokerEnabled ? [POKER_GAMES_OPTION] : []
	const allOptions = isPokerEnabled && casinoOption ? [ALL_GAMES_OPTION] : []

	if (hasExistingPokerTimeout && hasExistingCasinoTimeout) {
		return []
	} else if (hasExistingPokerTimeout) {
		return casinoOption
	} else if (hasExistingCasinoTimeout) {
		return pokerOption
	} else {
		return [].concat(pokerOption, casinoOption, allOptions)
	}
}

export const responsibleGamingReducer = createReducer(INITIAL_STATE, HANDLERS)

export enum TimePeriod {
	DAY = 'DAY',
	WEEK = 'WEEK',
	MONTH = 'MONTH',
	HARD = 'HARD',
}

